import { useState } from 'react';
import type { CheckoutBookingDraft } from '../model/checkout_source.js';
import type { UseMultiTravelerCheckoutFormResult } from './use_multi_traveler_checkout_form.js';

/** O que os dois modos do checkout fazem igual ao enviar e ao fechar: o
 *  portão do envio, as entradas por viajante do builder e o rascunho de
 *  reserva devolvido ao host. As REGRAS de cada modo (preço, política, campos,
 *  pedido) ficam nos componentes de modo. */
export function useCheckoutSession({
  form,
  hostSubmitting,
  closeOnSubmit,
  onClose,
}: {
  form: UseMultiTravelerCheckoutFormResult;
  /** O ocupado do host vence o local: com `closeOnSubmit=false` o drawer fica
   *  montado durante a reserva e é o host quem liga/desliga o busy (uma falha
   *  determinada precisa REABILITAR o CTA — um lock local ficaria preso). */
  hostSubmitting: boolean | undefined;
  closeOnSubmit: boolean | undefined;
  onClose: (draft?: CheckoutBookingDraft) => void;
}) {
  // Guarda um duplo clique rápido no fire-and-forget: `onSubmit`+`onClose`
  // disparam juntos, mas o `open` do pai só desmonta o drawer um render depois.
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const submitting = hostSubmitting ?? localSubmitting;

  /** Despacha o envio pronto. `closeOnSubmit=false`: o ciclo é do host, que
   *  trava, fecha ou reabre. */
  const send = (dispatch: () => void) => {
    if (closeOnSubmit === false) {
      dispatch();
      return;
    }
    setLocalSubmitting(true);
    dispatch();
    onClose();
  };

  /** As entradas por viajante do builder de envio. */
  const travelerEntries = () =>
    form.travelers.map((slice) => {
      if (!slice.form) throw new Error('formulário do viajante ausente — reabra o checkout');
      return {
        traveler: slice.traveler,
        form: slice.form,
        allocations: slice.allocations,
        travelerDraft: slice.travelerDraft,
      };
    });

  // Fechar sem enviar devolve o rascunho de reserva — o host da MESMA cotação
  // ou do MESMO pedido re-seeda na reabertura (CC-4). Campos por viajante ficam
  // de fora de propósito: re-seedam do form corporativo buscado de novo.
  // Ocupado ⇒ no-op: a reserva em voo é ininterruptível.
  const close = () => {
    if (submitting) return;
    onClose({
      ...(form.motiveId ? { motiveId: form.motiveId } : {}),
      ...(form.justificationId ? { justificationId: form.justificationId } : {}),
      ...(form.observation ? { observation: form.observation } : {}),
    });
  };

  return { submitting, send, travelerEntries, close };
}
